import type { PnlQueryStats } from 'src/components/star-grid/queryStats.types';
import { reportQueryStats } from 'src/components/star-grid/utils/reportQueryStats';
import React from 'react';
import type { StarRocksQuery } from 'src/components/star-grid/api/reportEngineTypes';
import { ReportStatsPanel } from 'src/components/star-grid/components/ReportStatsPanel';
import type { QueryStatsSource } from 'src/components/star-grid/components/ReportStatsSummary';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { formatReportLoadSeconds } from 'src/components/star-grid/utils/reportLoadTime';
import styles from 'src/components/star-grid/views/styles/ReportDetailView.module.scss';
export const ReportLoadStats: React.FC<{
  durationMs?: number;
  loading: boolean;
  serverDurationMs?: number | null;
  queryStats?: PnlQueryStats;
  /** Extra counters to show after the load time, for engines that report no server-side stats. */
  extraStats?: readonly string[];
  /** Every StarRocks query the last load ran; falls back to the profiled statement when absent. */
  queries?: readonly StarRocksQuery[];
  /** Where the panel reads the engine counters from when opened, instead of on every load. */
  statsSource?: QueryStatsSource;
  /** StarRocks' plans for the last report query, planned again on demand. */
  onExplain?: () => Promise<readonly string[]>;
}> = ({ durationMs, loading, serverDurationMs, queryStats, extraStats, queries, statsSource, onExplain }) => {
  const text = useStarGridText();
  const stats = reportQueryStats(loading ? undefined : serverDurationMs, loading ? undefined : queryStats, text);
  const sql = queryStats?.sql?.trim();
  const ran = queries ?? (sql ? [{ sql, ms: queryStats?.wall_time_ms ?? 0, rows: 0, bypass_cache: false }] : []);
  if (!loading && durationMs === undefined) return null;
  return (
    <>
      <span
        className={styles.loadStats}
        role="status"
        title={[text('reportLoadTimeHelp'), ...stats.details].join('\n')}
        data-testid="report-load-time"
      >
        {text('reportLoadTime')}:{' '}
        <strong>{loading ? text('reportLoadInProgress') : `${formatReportLoadSeconds(durationMs!)} ${text('reportSecondsUnit')}`}</strong>
        {stats.inline && <span data-testid="report-engine-stats"> · {stats.inline}</span>}
        {!loading && extraStats && extraStats.length > 0 && <span data-testid="report-extra-stats"> · {extraStats.join(' · ')}</span>}
      </span>
      {!loading && (
        <ReportStatsPanel queries={ran} serverMs={serverDurationMs} queryStats={queryStats} statsSource={statsSource} onExplain={onExplain} />
      )}
    </>
  );
};
